import { ReactNode, useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Users, Bell, LogOut, Grid3x3 as Grid3X3, LayoutGrid } from 'lucide-react';
import { useChAuth } from '../contexts/ChAuthContext';
import { chNotificationService } from '../services/notificationService';
import { supabase } from '../../lib/supabase';

interface NavItem {
  path: string;
  label: string;
  icon: typeof LayoutDashboard;
  adminOnly?: boolean;
}

const NAV_ITEMS: NavItem[] = [
  { path: '/contenthub', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/contenthub/projetos', label: 'Projetos', icon: FolderKanban },
  { path: '/contenthub/utilizadores', label: 'Utilizadores', icon: Users, adminOnly: true },
  { path: '/contenthub/notificacoes', label: 'Notificações', icon: Bell },
];

export function ChLayout({ children }: { children: ReactNode }) {
  const { user, signOut } = useChAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasMainAppAccess, setHasMainAppAccess] = useState(false);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data && (data.role === 'admin' || data.role === 'agent')) {
          setHasMainAppAccess(true);
        }
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    chNotificationService.getUnreadCount(user.id).then(setUnreadCount);

    const unsubscribe = chNotificationService.subscribeToNew(user.id, () => {
      setUnreadCount(prev => prev + 1);
    });

    return unsubscribe;
  }, [user]);

  useEffect(() => {
    if (location.pathname === '/contenthub/notificacoes' && user) {
      chNotificationService.getUnreadCount(user.id).then(setUnreadCount);
    }
  }, [location.pathname, user]);

  const visibleNav = NAV_ITEMS.filter(item => {
    if (item.adminOnly && user?.role !== 'admin') return false;
    return true;
  });

  const isActive = (path: string) => {
    if (path === '/contenthub') return location.pathname === '/contenthub';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="ch-app flex min-h-screen">
      {/* Desktop sidebar */}
      <aside className="ch-sidebar hidden md:flex">
        <div className="ch-sidebar-logo flex items-center justify-between">
          <span>Mohit Content Hub</span>
          <div className="flex items-center gap-1">
            {hasMainAppAccess && (
              <button
                onClick={() => navigate('/main')}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="App Principal"
              >
                <LayoutGrid className="w-4 h-4 text-white/60 hover:text-white" />
              </button>
            )}
            {hasMainAppAccess && (
              <button
                onClick={() => navigate('/apps?creators=1')}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title="Creators Hub"
              >
                <Users className="w-4 h-4 text-white/60 hover:text-white" />
              </button>
            )}
            <button
              onClick={() => navigate('/apps')}
              className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              title="Módulos"
            >
              <Grid3X3 className="w-4 h-4 text-white/60 hover:text-white" />
            </button>
          </div>
        </div>

        <nav className="flex-1 space-y-1">
          {visibleNav.map(item => (
            <button
              key={item.path}
              onClick={() => navigate(item.path)}
              className={`ch-nav-item w-full text-left ${isActive(item.path) ? 'ch-nav-item-active' : ''}`}
            >
              <div className="relative">
                <item.icon className="w-[18px] h-[18px]" />
                {item.path === '/contenthub/notificacoes' && unreadCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-[var(--ch-magenta)] text-white text-[10px] font-bold flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="mt-auto pt-4 border-t border-white/10">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[var(--ch-lime)] flex items-center justify-center text-xs font-bold text-[var(--ch-ink)]">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-white/40 truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={signOut}
            className="ch-nav-item w-full text-left text-red-300 hover:text-red-200 hover:bg-red-500/10"
          >
            <LogOut className="w-[18px] h-[18px]" />
            <span>Terminar sessão</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="flex-1 overflow-y-auto pb-20 md:pb-0">
        {children}
      </main>

      {/* Mobile bottom bar */}
      <nav className="ch-bottombar md:hidden">
        {visibleNav.map(item => (
          <button
            key={item.path}
            onClick={() => navigate(item.path)}
            className={`ch-bottombar-item ${isActive(item.path) ? 'ch-bottombar-item-active' : ''}`}
          >
            <div className="relative">
              <item.icon className="w-5 h-5" />
              {item.path === '/contenthub/notificacoes' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1.5 min-w-[14px] h-3.5 px-1 rounded-full bg-[var(--ch-magenta)] text-white text-[9px] font-bold flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </span>
              )}
            </div>
            <span>{item.label}</span>
          </button>
        ))}
      </nav>
    </div>
  );
}
